import React, { useState, useMemo, memo, useCallback } from "react";
import {
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Switch,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ScienceIcon from "@mui/icons-material/Science";
import {
  Chart,
  CommonSeriesSettings,
  Series,
  ValueAxis,
  ArgumentAxis,
  Label,
  Export,
  Tooltip,
  Legend,
  Size,
  Title,
  Grid,
  Point,
  Strip,
} from "devextreme-react/chart";
import DataGrid, {
  Column,
  Sorting,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Export as GridExport,
  MasterDetail,
  GroupPanel,
  Grouping,
  Summary,
  GroupItem,
} from "devextreme-react/data-grid";
import { useFetch } from "src/hooks/useFetch";
import {
  CytoQCStatus,
  getWellStatus,
  getRunStatus,
  getDispositionColor,
  getBatchAssignedColor,
  STATUS_COLORS,
  isValid,
} from "src/utils/cyto-qc-grading";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: { default: "#121212", paper: "#1e1e2f" },
  },
});

interface BatchStat {
  batchName: string;
  assayType: string;
  cycles: number;
  meanAssignedCountPerCell: number;
  assignedCountsPerMm2: number;
  extracellularRatio: number;
  percentCellularAssigned: number;
  gapdhDensity: number;
  nsbControl: number;
  assignedRate: number;
  mismatchRate: number;
}

interface WellStat {
  wellLocation: string;
  wellLabel: string;
  cellCount: number;
  cellDiameter: number;
  confluency: number;
  nucleatedPct: number;
  assignedRate: number;
  mismatchRate: number;
  extracellularRatio: number;
  assignedPerMm2: number;
  gapdhDensity: number;
  nsbControl: number;
  batches?: BatchStat[];
  status: CytoQCStatus;
}

interface CytoRunStat {
  id: string;
  runId: string;
  runName: string;
  flowCellId: string;
  tenantId: string;
  instrumentId: string;
  instrumentName: string;
  cartridgeType: string;
  analysisVersion: string;
  numWells: number;
  numBatches: number;
  totalPolonies: number;
  totalCellCount: number;
  overallAssignedRate: number;
  overallMismatchRate: number;
  overallConfluency: number;
  overallNucleatedPct: number;
  overallAssignedPerMm2: number;
  overallExtracellularRatio: number;
  wellPassCount: number;
  wellWarningCount: number;
  wellCriticalCount: number;
  wellStats: WellStat[];
  runDate: string;
  created: string;
}

const statusIcon = (status: CytoQCStatus, size: number = 16) => {
  switch (status) {
    case "pass":
      return <CheckCircleIcon sx={{ color: "#4caf50", fontSize: size }} />;
    case "fail":
      return <CancelIcon sx={{ color: "#f44336", fontSize: size }} />;
  }
};

function computedAssignedPerMm2(w: WellStat): number {
  const batches = (w.batches || []).filter(
    (b) => !b.batchName.startsWith("CP")
  );
  const valid = batches.map((b) => b.assignedCountsPerMm2).filter(isValid);
  return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) : w.assignedPerMm2;
}

function skippedMetrics(cartridgeType: string): string[] | undefined {
  return cartridgeType !== "KitTypeCytoprofiling"
    ? ["assignedPerMm2"]
    : undefined;
}

function gradeWell(w: WellStat, cartridgeType: string): CytoQCStatus {
  return getWellStatus(
    { ...w, assignedPerMm2: computedAssignedPerMm2(w) },
    skippedMetrics(cartridgeType)
  );
}

function getOverallStatus(run: CytoRunStat): CytoQCStatus {
  if (run.wellStats && run.wellStats.length > 0) {
    const statuses = run.wellStats.map((w) => gradeWell(w, run.cartridgeType));
    return getRunStatus(statuses);
  }
  const total =
    run.wellPassCount + run.wellWarningCount + run.wellCriticalCount;
  if (total === 0) return "pass";
  const failCount = run.wellCriticalCount + run.wellWarningCount;
  return failCount / total > 0.25 ? "fail" : "pass";
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatNumber(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toString();
}

const DATE_RANGE_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
  { value: 365, label: "Last year" },
];

const CytoQCMultiRunDashboard = memo(() => {
  const [dateRange, setDateRange] = useState(90);
  const [instrumentFilter, setInstrumentFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showInternalRuns, setShowInternalRuns] = useState(false);

  const customerOnly = showInternalRuns ? "false" : "true";
  const { data: rawData, isLoading } = useFetch(
    `/cytoRunStats?days=${dateRange}&customerOnly=${customerOnly}`,
    true
  );

  const runs = useMemo(() => {
    if (!rawData || !Array.isArray(rawData)) return [];
    return rawData as CytoRunStat[];
  }, [rawData]);

  const instruments = useMemo(() => {
    const set = new Set(runs.map((r) => r.instrumentName));
    return Array.from(set).sort();
  }, [runs]);

  const filteredRuns = useMemo(() => {
    let result = runs;
    if (instrumentFilter !== "All") {
      result = result.filter((r) => r.instrumentName === instrumentFilter);
    }
    if (statusFilter !== "All") {
      result = result.filter((r) => getOverallStatus(r) === statusFilter);
    }
    return result;
  }, [runs, instrumentFilter, statusFilter]);

  const summaryStats = useMemo(() => {
    if (filteredRuns.length === 0)
      return {
        totalRuns: 0,
        avgAssignedRate: 0,
        avgMismatchRate: 0,
        avgConfluency: 0,
        avgNucleated: 0,
        passCount: 0,
        failCount: 0,
      };

    const n = filteredRuns.length;
    const validAvg = (vals: number[]) => {
      const valid = vals.filter(isValid);
      return valid.length > 0
        ? valid.reduce((a, b) => a + b, 0) / valid.length
        : 0;
    };
    return {
      totalRuns: n,
      avgAssignedRate: validAvg(filteredRuns.map((r) => r.overallAssignedRate)),
      avgMismatchRate: validAvg(filteredRuns.map((r) => r.overallMismatchRate)),
      avgConfluency: validAvg(filteredRuns.map((r) => r.overallConfluency)),
      avgNucleated: validAvg(filteredRuns.map((r) => r.overallNucleatedPct)),
      passCount: filteredRuns.filter((r) => getOverallStatus(r) === "pass")
        .length,
      failCount: filteredRuns.filter((r) => getOverallStatus(r) === "fail")
        .length,
    };
  }, [filteredRuns]);

  const trendData = useMemo(() => {
    return filteredRuns
      .sort(
        (a, b) => new Date(a.runDate).getTime() - new Date(b.runDate).getTime()
      )
      .map((r) => ({
        date: formatDate(r.runDate),
        runName: r.runName,
        instrument: r.instrumentName,
        assignedRate: isValid(r.overallAssignedRate)
          ? r.overallAssignedRate
          : undefined,
        mismatchRate: isValid(r.overallMismatchRate)
          ? r.overallMismatchRate
          : undefined,
        confluency: isValid(r.overallConfluency)
          ? r.overallConfluency
          : undefined,
        nucleatedPct: isValid(r.overallNucleatedPct)
          ? r.overallNucleatedPct
          : undefined,
        extracellularRatio: isValid(r.overallExtracellularRatio)
          ? r.overallExtracellularRatio * 100
          : undefined,
      }));
  }, [filteredRuns]);

  const instrumentComparisonData = useMemo(() => {
    const byInstrument: Record<string, { name: string; runs: CytoRunStat[] }> =
      {};
    for (const r of filteredRuns) {
      if (!byInstrument[r.instrumentName]) {
        byInstrument[r.instrumentName] = { name: r.instrumentName, runs: [] };
      }
      byInstrument[r.instrumentName].runs.push(r);
    }
    const validAvg = (vals: number[]) => {
      const valid = vals.filter(isValid);
      return valid.length > 0
        ? valid.reduce((a, b) => a + b, 0) / valid.length
        : 0;
    };
    return Object.values(byInstrument).map((group) => ({
      instrument: group.name,
      avgAssignedRate: validAvg(group.runs.map((r) => r.overallAssignedRate)),
      avgMismatchRate: validAvg(group.runs.map((r) => r.overallMismatchRate)),
      avgConfluency: validAvg(group.runs.map((r) => r.overallConfluency)),
      runCount: group.runs.length,
    }));
  }, [filteredRuns]);

  const gridData = useMemo(() => {
    return filteredRuns.map((r) => ({
      ...r,
      status: getOverallStatus(r),
      dateFormatted: formatDate(r.runDate),
      totalCellCountFormatted: formatNumber(r.totalCellCount),
      totalPoloniesFormatted: formatNumber(r.totalPolonies),
    }));
  }, [filteredRuns]);

  const runNameCellRender = useCallback(
    (cellData: { data: { runId: string; runName: string } }) => {
      const href = `#/spatialRuns?r=${encodeURIComponent(
        cellData.data.runId
      )}&t=Run%20Review`;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#90caf9",
            textDecoration: "none",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          {cellData.data.runName}
        </a>
      );
    },
    []
  );

  const statusCellRender = useCallback((cellData: { value: CytoQCStatus }) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {statusIcon(cellData.value, 14)}
        <span style={{ textTransform: "capitalize" }}>{cellData.value}</span>
      </Box>
    );
  }, []);

  const wellStatusCellRender = useCallback(
    (cellData: { data: CytoRunStat }) => {
      const r = cellData.data;
      const wells = r.wellStats || [];
      const statuses = wells.map((w) => gradeWell(w, r.cartridgeType));
      const passCount =
        wells.length > 0
          ? statuses.filter((s) => s === "pass").length
          : r.wellPassCount;
      const failCount =
        wells.length > 0
          ? statuses.filter((s) => s === "fail").length
          : r.wellCriticalCount + r.wellWarningCount;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
            {statusIcon("pass", 12)}
            <span>{passCount}</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
            {statusIcon("fail", 12)}
            <span>{failCount}</span>
          </Box>
        </Box>
      );
    },
    []
  );

  const pctCellRender = useCallback(
    (cellData: { value: number }) => (
      <span>
        {isValid(cellData.value) ? `${cellData.value.toFixed(1)}%` : "—"}
      </span>
    ),
    []
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          bgcolor: "#121212",
          color: "#e0e0e0",
          minHeight: "calc(100vh - 80px)",
          p: 3,
          ml: "80px",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "#121212",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}
            >
              Cyto Run QC Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              Performance overview across all cyto runs
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: "#999" }}>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value as number)}
                sx={{
                  color: "#ccc",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666",
                  },
                  ".MuiSvgIcon-root": { color: "#999" },
                }}
              >
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, display: "none" }}>
              <InputLabel sx={{ color: "#999" }}>Instrument</InputLabel>
              <Select
                value={instrumentFilter}
                label="Instrument"
                onChange={(e) => setInstrumentFilter(e.target.value)}
                sx={{
                  color: "#ccc",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666",
                  },
                  ".MuiSvgIcon-root": { color: "#999" },
                }}
              >
                <MenuItem value="All">All Instruments</MenuItem>
                {instruments.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130, display: "none" }}>
              <InputLabel sx={{ color: "#999" }}>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{
                  color: "#ccc",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666",
                  },
                  ".MuiSvgIcon-root": { color: "#999" },
                }}
              >
                <MenuItem value="All">All Statuses</MenuItem>
                <MenuItem value="pass">Pass</MenuItem>
                <MenuItem value="fail">Fail</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={showInternalRuns}
                  onChange={(e) => setShowInternalRuns(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#7c4dff" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#7c4dff",
                    },
                  }}
                />
              }
              label="Show Internal Runs"
              sx={{ color: "#999", ml: 1 }}
            />
          </Box>
        </Box>

        {/* Summary Cards */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <SummaryCard
            label="Total Runs"
            value={summaryStats.totalRuns.toString()}
            icon={<ScienceIcon sx={{ color: "#7c4dff", fontSize: 28 }} />}
          />
          <SummaryCard
            label="Avg. Assigned Rate"
            value={`${summaryStats.avgAssignedRate.toFixed(1)}%`}
            icon={<TrendingUpIcon sx={{ color: "#4caf50", fontSize: 28 }} />}
            subtitle="Target: > 65%"
          />
          <SummaryCard
            label="Avg. Mismatch Rate"
            value={`${summaryStats.avgMismatchRate.toFixed(1)}%`}
            icon={<TrendingDownIcon sx={{ color: "#ff9800", fontSize: 28 }} />}
            subtitle="Target: < 30%"
          />
          <SummaryCard
            label="Avg. Confluency"
            value={`${summaryStats.avgConfluency.toFixed(1)}%`}
            icon={<TrendingUpIcon sx={{ color: "#00bcd4", fontSize: 28 }} />}
            subtitle="Target: 10-70%"
          />
          <Box
            sx={{
              background: "rgba(255,255,255,0.03)",
              borderRadius: 2,
              border: "1px solid #2a2a2a",
              p: 2,
              minWidth: 180,
              flex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#777", display: "block", mb: 1 }}
            >
              Status Distribution
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {statusIcon("pass", 18)}
                <Typography
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 600 }}
                >
                  {summaryStats.passCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {statusIcon("fail", 18)}
                <Typography
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 600 }}
                >
                  {summaryStats.failCount}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Trend Charts */}
        {trendData.length > 0 && (
          <Box sx={{ mb: 3, display: "none" }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 600, mb: 2 }}
            >
              Metric Trends Over Time
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TrendChart
                title="Assigned Rate (%)"
                data={trendData}
                field="assignedRate"
                color="#4caf50"
                warningLine={65}
                errorLine={50}
              />
              <TrendChart
                title="Mismatch Rate (%)"
                data={trendData}
                field="mismatchRate"
                color="#ff9800"
                warningLine={30}
                errorLine={50}
                invertThresholds
              />
              <TrendChart
                title="Confluency (%)"
                data={trendData}
                field="confluency"
                color="#00bcd4"
              />
              <TrendChart
                title="Percent Nucleated (%)"
                data={trendData}
                field="nucleatedPct"
                color="#7c4dff"
                warningLine={90}
                errorLine={75}
              />
            </Box>
          </Box>
        )}

        {/* Instrument Comparison */}
        {instrumentComparisonData.length > 1 && (
          <Box sx={{ mb: 3, display: "none" }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 600, mb: 2 }}
            >
              Instrument Comparison
            </Typography>
            <Box
              sx={{
                background: "rgba(255,255,255,0.03)",
                borderRadius: 2,
                border: "1px solid #2a2a2a",
                p: 2,
              }}
            >
              <Chart dataSource={instrumentComparisonData}>
                <Size height={300} />
                <CommonSeriesSettings
                  argumentField="instrument"
                  type="bar"
                  barPadding={0.3}
                />
                <Series
                  valueField="avgAssignedRate"
                  name="Avg Assigned Rate"
                  color="#4caf50"
                />
                <Series
                  valueField="avgMismatchRate"
                  name="Avg Mismatch Rate"
                  color="#ff9800"
                />
                <Series
                  valueField="avgConfluency"
                  name="Avg Confluency"
                  color="#00bcd4"
                />
                <ValueAxis>
                  <Label
                    customizeText={(e: { value: number }) => `${e.value}%`}
                  />
                  <Grid visible opacity={0.1} />
                </ValueAxis>
                <ArgumentAxis>
                  <Label
                    wordWrap="none"
                    overlappingBehavior="rotate"
                    rotationAngle={-45}
                  />
                </ArgumentAxis>
                <Legend
                  verticalAlignment="top"
                  horizontalAlignment="right"
                  font={{ color: "#999" }}
                />
                <Tooltip enabled />
                <Export enabled />
              </Chart>
            </Box>
          </Box>
        )}

        {/* Run Table */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: "#fff", fontWeight: 600, mb: 2 }}
          >
            Run Details ({filteredRuns.length} runs)
          </Typography>
          <Box
            sx={{
              background: "rgba(255,255,255,0.03)",
              borderRadius: 2,
              border: "1px solid #2a2a2a",
              overflow: "hidden",
              "& .dx-datagrid": {
                backgroundColor: "transparent",
                color: "#e0e0e0",
              },
              "& .dx-datagrid-headers": {
                backgroundColor: "rgba(255,255,255,0.05)",
                color: "#999",
              },
              "& .dx-row": {
                borderBottom: "1px solid #2a2a2a",
              },
              "& .dx-datagrid-rowsview .dx-row": {
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.05) !important",
                },
              },
              "& .dx-datagrid-search-panel": {
                backgroundColor: "#2a2a3a",
                border: "1px solid #444",
                "& .dx-texteditor-input": { color: "#e0e0e0" },
                "& .dx-placeholder::before": { color: "#777" },
              },
              "& .dx-datagrid-filter-row": {
                backgroundColor: "#1e1e2f !important",
                "& td": {
                  backgroundColor: "#1e1e2f !important",
                },
                "& .dx-editor-cell": {
                  backgroundColor: "#1e1e2f !important",
                },
                "& .dx-texteditor": {
                  backgroundColor: "transparent !important",
                  border: "none",
                },
                "& .dx-texteditor-input": { color: "#e0e0e0" },
                "& .dx-placeholder::before": { color: "#555" },
                "& .dx-menu-item-content .dx-icon": { color: "#777" },
                "& .dx-editor-with-menu .dx-menu": {
                  backgroundColor: "transparent !important",
                },
              },
              "& .dx-header-filter": {
                color: "#777 !important",
              },
              "& .dx-datagrid-header-panel": {
                backgroundColor: "transparent",
                borderBottom: "1px solid #2a2a2a",
              },
              "& .dx-toolbar": {
                backgroundColor: "transparent",
              },
              "& .dx-datagrid-export-button .dx-icon": {
                color: "#999",
              },
              "& .dx-page, & .dx-info": {
                color: "#999",
              },
              "& .dx-page.dx-selection": {
                backgroundColor: "#7c4dff",
                color: "#fff",
              },
              "& .dx-datagrid-pager": {
                borderTop: "1px solid #2a2a2a",
              },
              "& .dx-group-panel": {
                backgroundColor: "transparent",
                borderBottom: "1px solid #2a2a2a",
              },
              "& .dx-group-panel-message": {
                color: "#777 !important",
              },
              "& .dx-group-panel-item": {
                backgroundColor: "#2a2a3a !important",
                border: "1px solid #444 !important",
                color: "#ccc !important",
                "& .dx-column-indicators .dx-icon": {
                  color: "#999 !important",
                },
                "& .dx-header-filter": {
                  color: "#777 !important",
                },
              },
              "& .dx-group-row": {
                backgroundColor: "rgba(255,255,255,0.04) !important",
                "& td": {
                  backgroundColor: "rgba(255,255,255,0.04) !important",
                  color: "#ccc !important",
                  borderBottom: "1px solid #333",
                },
              },
              "& .dx-datagrid-headers .dx-datagrid-table .dx-row > td": {
                color: "#999 !important",
              },
              "& .dx-datagrid-headers .dx-sort, & .dx-datagrid-headers .dx-sort-indicator, & .dx-datagrid-headers .dx-column-indicators .dx-sort":
                {
                  color: "#999 !important",
                },
              "& .dx-header-row .dx-datagrid-text-content": {
                color: "#999 !important",
              },
              "& .dx-master-detail-cell": {
                backgroundColor: "#1a1a2e !important",
                borderBottom: "1px solid #333",
              },
              "& .dx-datagrid-rowsview .dx-master-detail-row > td": {
                backgroundColor: "#1a1a2e !important",
              },
              "& .dx-datagrid-expand .dx-datagrid-group-closed, & .dx-datagrid-expand .dx-datagrid-group-opened":
                {
                  color: "#999",
                },
            }}
          >
            <DataGrid
              dataSource={gridData}
              showBorders={false}
              rowAlternationEnabled={false}
              columnAutoWidth
              wordWrapEnabled={false}
              noDataText={isLoading ? "Loading..." : "No runs found"}
            >
              <SearchPanel visible placeholder="Search runs..." />
              <FilterRow visible />
              <HeaderFilter visible />
              <GroupPanel visible />
              <Grouping autoExpandAll={false} />
              <Sorting mode="multiple" />
              <Paging defaultPageSize={20} />
              <GridExport enabled />

              <Column
                dataField="status"
                caption="Status"
                width={100}
                cellRender={statusCellRender}
                alignment="center"
              />
              <Column
                dataField="runName"
                caption="Run Name"
                width={160}
                cellRender={runNameCellRender}
              />
              <Column
                dataField="dateFormatted"
                caption="Date"
                width={120}
                sortOrder="desc"
                sortIndex={0}
                calculateSortValue="runDate"
              />
              <Column dataField="tenantId" caption="Tenant" width={140} />
              <Column
                dataField="instrumentName"
                caption="Instrument"
                width={140}
              />
              <Column
                dataField="cartridgeType"
                caption="Cartridge"
                width={180}
                cellRender={(cellData: { value: string }) => (
                  <span>{(cellData.value || "").replace(/^KitType/, "")}</span>
                )}
              />
              <Column
                dataField="totalCellCount"
                caption="Cell Count"
                width={100}
                cellRender={(c: {
                  data: { totalCellCountFormatted: string };
                }) => <span>{c.data.totalCellCountFormatted}</span>}
              />
              <Column
                dataField="overallAssignedRate"
                caption="Assigned %"
                width={100}
                cellRender={pctCellRender}
              />
              <Column
                dataField="overallMismatchRate"
                caption="Mismatch %"
                width={100}
                cellRender={pctCellRender}
              />
              <Column
                dataField="overallConfluency"
                caption="Confluency %"
                width={110}
                cellRender={pctCellRender}
              />
              <Column
                dataField="overallNucleatedPct"
                caption="Nucleated %"
                width={100}
                cellRender={pctCellRender}
              />
              <Column
                caption="Well Status"
                width={130}
                cellRender={wellStatusCellRender}
                allowSorting={false}
              />
              <Column
                dataField="numWells"
                caption="Wells"
                width={60}
                alignment="center"
              />
              <Column
                dataField="numBatches"
                caption="Batches"
                width={70}
                alignment="center"
              />
              <MasterDetail
                enabled
                render={(props) => <WellDetailRender {...props} />}
              />
              <Summary>
                <GroupItem summaryType="count" displayFormat="{0} runs" />
              </Summary>
            </DataGrid>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
});

function WellDetailRender({
  data,
}: {
  data: CytoRunStat & { wellStats?: WellStat[] };
}) {
  const [expandedWells, setExpandedWells] = useState<Set<string>>(new Set());
  const wells = data.wellStats;

  if (!wells || wells.length === 0) {
    return (
      <Box sx={{ p: 2, color: "#777" }}>
        <Typography variant="body2">
          No well-level details available for this run.
        </Typography>
      </Box>
    );
  }

  const toggleWell = (wellLoc: string) => {
    setExpandedWells((prev) => {
      const next = new Set(prev);
      if (next.has(wellLoc)) next.delete(wellLoc);
      else next.add(wellLoc);
      return next;
    });
  };

  const isCytoprofiling = data.cartridgeType === "KitTypeCytoprofiling";

  const fmt = (v: number, decimals: number, suffix = "") =>
    isValid(v) ? `${v.toFixed(decimals)}${suffix}` : "—";

  const fmtK = (v: number) =>
    isValid(v) ? `${(v / 1000).toFixed(1)} k/mm²` : "—";

  const coloredCell = (
    v: number,
    metric: string,
    decimals: number,
    suffix = ""
  ) => (
    <td style={{ color: getDispositionColor(v, metric) || "#ddd" }}>
      {fmt(v, decimals, suffix)}
    </td>
  );

  const coloredCellK = (v: number, metric: string) => (
    <td style={{ color: getDispositionColor(v, metric) || "#ddd" }}>
      {fmtK(v)}
    </td>
  );

  const plainCell = (v: number, decimals: number, suffix = "") => (
    <td style={{ color: "#ddd" }}>{fmt(v, decimals, suffix)}</td>
  );

  const plainCellK = (v: number) => (
    <td style={{ color: "#ddd" }}>{fmtK(v)}</td>
  );

  return (
    <Box sx={{ p: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: "#bbb", mb: 1, fontWeight: 600 }}
      >
        Well Details — {data.runName}
      </Typography>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.8rem",
          "& th": {
            color: "#888",
            fontWeight: 600,
            textAlign: "left",
            px: 1.5,
            py: 0.75,
            borderBottom: "1px solid #333",
            whiteSpace: "nowrap",
          },
          "& td": {
            color: "#ddd",
            textAlign: "left",
            px: 1.5,
            py: 0.75,
            borderBottom: "1px solid #222",
            whiteSpace: "nowrap",
          },
          "& tr:hover td": {
            backgroundColor: "rgba(255,255,255,0.03)",
          },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <th>Status</th>
            <th>Well</th>
            <th>Label</th>
            <th>Assay Type</th>
            <th>Cell Count</th>
            <th>Diameter (μm)</th>
            <th>Confluency %</th>
            <th>Nucleated %</th>
            <th>Assigned %</th>
            <th>Mismatch %</th>
            <th>Extracellular Ratio</th>
            <th>Assigned (k/mm²)</th>
            <th>GAPDH (k/mm²)</th>
            <th>NSB (k/mm²)</th>
          </tr>
        </thead>
        <tbody>
          {wells.map((w) => {
            const status = gradeWell(w, data.cartridgeType);
            const allBatches = w.batches || [];
            const hasBatches = allBatches.length > 0;
            const isExpanded = expandedWells.has(w.wellLocation);
            return (
              <React.Fragment key={w.wellLocation}>
                <tr
                  style={{ cursor: hasBatches ? "pointer" : "default" }}
                  onClick={() => hasBatches && toggleWell(w.wellLocation)}
                >
                  <td style={{ textAlign: "center", padding: "0 4px" }}>
                    {hasBatches && (
                      <span style={{ color: "#777", fontSize: "0.7rem" }}>
                        {isExpanded ? "▼" : "▶"}
                      </span>
                    )}
                  </td>
                  <td>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: STATUS_COLORS[status],
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          textTransform: "capitalize",
                          fontSize: "0.75rem",
                        }}
                      >
                        {status}
                      </span>
                    </Box>
                  </td>
                  <td>{w.wellLocation}</td>
                  <td>{w.wellLabel}</td>
                  <td />
                  <td>{w.cellCount.toLocaleString()}</td>
                  <td>{fmt(w.cellDiameter, 1)}</td>
                  {plainCell(w.confluency, 1, "%")}
                  {coloredCell(w.nucleatedPct, "nucleatedPct", 1, "%")}
                  {coloredCell(w.assignedRate, "assignedRate", 1, "%")}
                  {plainCell(w.mismatchRate, 1, "%")}
                  {coloredCell(w.extracellularRatio, "extracellularRatio", 2)}
                  {(() => {
                    const batchVals = allBatches
                      .filter((b) => !b.batchName.startsWith("CP"))
                      .map((b) => b.assignedCountsPerMm2)
                      .filter(isValid);
                    const sumPerMm2 =
                      batchVals.length > 0
                        ? batchVals.reduce((a, b) => a + b, 0)
                        : w.assignedPerMm2;
                    return isCytoprofiling
                      ? coloredCellK(sumPerMm2, "assignedPerMm2")
                      : plainCellK(sumPerMm2);
                  })()}
                  {plainCellK(w.gapdhDensity)}
                  {plainCellK(w.nsbControl)}
                </tr>
                {isExpanded &&
                  allBatches.map((b) => (
                    <tr
                      key={`${w.wellLocation}-${b.batchName}`}
                      style={{ backgroundColor: "rgba(255,255,255,0.02)" }}
                    >
                      <td />
                      <td />
                      <td
                        style={{
                          color: "#7c4dff",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      >
                        {b.batchName}
                      </td>
                      <td />
                      <td
                        style={{
                          color: "#aaa",
                          fontSize: "0.75rem",
                        }}
                      >
                        {b.assayType
                          ? b.cycles
                            ? `${b.assayType} (${b.cycles})`
                            : b.assayType
                          : ""}
                      </td>
                      <td />
                      <td />
                      <td />
                      <td />
                      {coloredCell(b.assignedRate, "assignedRate", 1, "%")}
                      {plainCell(b.mismatchRate, 1, "%")}
                      {coloredCell(
                        b.extracellularRatio,
                        "extracellularRatio",
                        3
                      )}
                      {isCytoprofiling ? (
                        <td
                          style={{
                            color:
                              getBatchAssignedColor(b.assignedCountsPerMm2) ||
                              "#ddd",
                          }}
                        >
                          {fmtK(b.assignedCountsPerMm2)}
                        </td>
                      ) : (
                        plainCellK(b.assignedCountsPerMm2)
                      )}
                      {plainCellK(b.gapdhDensity)}
                      {plainCellK(b.nsbControl)}
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </Box>
      {wells.some((w) => w.batches && w.batches.length > 0) && (
        <Typography
          variant="caption"
          sx={{ color: "#555", mt: 0.5, display: "block" }}
        >
          Click a well row to expand/collapse batch details
        </Typography>
      )}
    </Box>
  );
}

const SummaryCard = ({
  label,
  value,
  icon,
  subtitle,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  subtitle?: string;
}) => (
  <Box
    sx={{
      background: "rgba(255,255,255,0.03)",
      borderRadius: 2,
      border: "1px solid #2a2a2a",
      p: 2,
      minWidth: 160,
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    {icon}
    <Box>
      <Typography variant="caption" sx={{ color: "#777" }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{ color: "#555", fontSize: "0.65rem" }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const TrendChart = ({
  title,
  data,
  field,
  color,
  warningLine,
  errorLine,
  invertThresholds,
}: {
  title: string;
  data: Record<string, unknown>[];
  field: string;
  color: string;
  warningLine?: number;
  errorLine?: number;
  invertThresholds?: boolean;
}) => (
  <Box
    sx={{
      background: "rgba(255,255,255,0.03)",
      borderRadius: 2,
      border: "1px solid #2a2a2a",
      p: 2,
      flex: "1 1 48%",
      minWidth: 400,
    }}
  >
    <Chart dataSource={data}>
      <Size height={250} />
      <Title text={title} font={{ color: "#ccc", size: 14 }} />
      <CommonSeriesSettings argumentField="date" type="line" />
      <Series valueField={field} name={title} color={color}>
        <Point size={6} />
      </Series>
      <ValueAxis>
        <Label customizeText={(e: { value: number }) => `${e.value}%`} />
        <Grid visible opacity={0.1} />
        {warningLine != null && (
          <Strip
            startValue={invertThresholds ? warningLine : undefined}
            endValue={invertThresholds ? undefined : warningLine}
            color="rgba(255, 152, 0, 0.08)"
          />
        )}
        {errorLine != null && (
          <Strip
            startValue={invertThresholds ? errorLine : undefined}
            endValue={invertThresholds ? undefined : errorLine}
            color="rgba(244, 67, 54, 0.08)"
          />
        )}
      </ValueAxis>
      <ArgumentAxis>
        <Label
          wordWrap="none"
          overlappingBehavior="rotate"
          rotationAngle={-45}
          font={{ color: "#777", size: 10 }}
        />
      </ArgumentAxis>
      <Tooltip
        enabled
        customizeTooltip={(arg: {
          argumentText: string;
          valueText: string;
          seriesName: string;
        }) => ({
          text: `${arg.argumentText}\n${arg.seriesName}: ${arg.valueText}%`,
        })}
      />
      <Legend visible={false} />
      <Export enabled />
    </Chart>
  </Box>
);

export default CytoQCMultiRunDashboard;
